"use client";

import { Image } from "@pamperedchef/design-system";
import type { CmsImage } from "@/composites/landing/types";
import * as styles from "./ResponsiveImage.module.scss";

export interface ResponsiveImageProps {
	desktop?: CmsImage;
	mobile?: CmsImage;
	/** Overrides whatever alt either image carries. */
	alt?: string;
}

/**
 * Single-image responsive media block. Native `<picture>` swaps the source
 * at the md (768px) breakpoint; the rendered element underneath is the DS
 * atomic `Image`, so layout / borderRadius / objectFit follow DS contract.
 * Renders nothing when neither image is supplied.
 *
 * Used by the SocialProof block now that CMS ships dedicated desktop/mobile
 * assets in place of the legacy collage. Either field may be omitted; the
 * one that's present is used for both breakpoints.
 */
export function ResponsiveImage({
	desktop,
	mobile,
	alt
}: ResponsiveImageProps) {
	const primary = mobile ?? desktop;
	if (!primary) return null;

	const resolvedAlt = alt ?? primary.alt ?? "";

	return (
		<picture className={styles["responsive-image"]}>
			{desktop ? (
				<source media="(min-width: 768px)" srcSet={desktop.url} />
			) : null}
			<Image
				src={primary.url}
				alt={resolvedAlt}
				className={styles["responsive-image__img"]}
				objectFit="cover"
				loading="lazy"
				decoding="async"
			/>
		</picture>
	);
}
